// src/pages/User/RoadsideInspectionList.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Accordion,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import Select from "react-select";
import Pagination from "../../components/Pagination";

const RoadsideInspectionList = () => {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [filterSavedBy, setFilterSavedBy] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    axios
      .get(`${API_BASE}/api/roadside/${userId}`, { headers })
      .then((res) => {
        setRecordsUnfiltered(res.data || []);
        setRecords(res.data || []);
      })
      .catch((err) => console.error("❌ Fetch roadside inspections", err));
  }, [API_BASE, userId]);

  const applyFilters = () => {
    let filtered = [...recordsUnfiltered];

    if (filterDate) {
      filtered = filtered.filter(
        (i) =>
          i.ri_inspection_date &&
          new Date(i.ri_inspection_date).toDateString() ===
            new Date(filterDate).toDateString()
      );
    }

    if (filterSavedBy?.length) {
      const selectedUsers = filterSavedBy.map((s) => s.value);
      filtered = filtered.filter((i) =>
        selectedUsers.includes(i.saved_by_name)
      );
    }

    setRecords(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterDate("");
    setFilterSavedBy(null);
    setRecords(recordsUnfiltered);
  };

  const savedByOptions = [
    ...new Set(recordsUnfiltered.map((i) => i.saved_by_name).filter(Boolean)),
  ].map((s) => ({ label: s, value: s }));

  const paginatedRecords = records.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goBack = () => {
    if (isAdmin && paramId) navigate(`/userhome/${paramId}`);
    else navigate("/userhome");
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Roadside Inspections</h3>
        <div>
          <Button variant="secondary" className="me-2" onClick={goBack}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/roadside/${userId}/new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Accordion defaultActiveKey="">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body>
                <Form className="mb-4">
                  <Row className="align-items-end g-3">
                    <Col md={3}>
                      <Form.Label>Inspection Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Saved By</Form.Label>
                      <Select
                        isMulti
                        options={savedByOptions}
                        value={filterSavedBy}
                        onChange={setFilterSavedBy}
                        placeholder="Select Saved By"
                      />
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="mt-3"
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="success"
                        onClick={applyFilters}
                        className="mt-3"
                      >
                        Filter
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">
              No roadside inspections found.
            </p>
          ) : (
            <div className="table-responsive mt-3">
              <table className="table table-bordered align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th>Status</th>
                    <th>Report #</th>
                    <th>DOT</th>
                    <th>Inspection Date</th>
                    <th>Inspection Level</th>
                    <th>Violations</th>
                    <th>Last Updated Name</th>
                    <th>Last Updated Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((r) => {
                    const violCount = Array.isArray(r.ri_violations)
                      ? r.ri_violations.length
                      : (() => {
                          try {
                            const arr = JSON.parse(r.ri_violations || "[]");
                            return arr.length;
                          } catch {
                            return 0;
                          }
                        })();

                    return (
                      <tr key={r.ri_id}>
                        <td>{r.ri_is_clean ? "Clean" : "Action"}</td>
                        <td>{r.ri_number || "-"}</td>
                        <td>{r.ri_dot_flag ? "Yes" : "No"}</td>
                        <td>
                          {r.ri_inspection_date
                            ? new Date(
                                r.ri_inspection_date
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>{r.ri_inspection_level || "-"}</td>
                        <td>{violCount}</td>
                        <td>{r.saved_by_name || "-"}</td>
                        <td>
                          {r.ri_saved_date
                            ? new Date(r.ri_saved_date).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() =>
                              navigate(`/roadside/${userId}/detail/${r.ri_id}`)
                            }
                          >
                            ✎
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RoadsideInspectionList;

